import { useEffect, useState } from "react";
import { getAdminRefunds } from "../../../api/api-hook";
import { hubspotRecordUrl } from "../../../utils/hubspotIds";

type Refund = {
  dealId: string;
  contactId: string;
  firstName: string;
  lastName: string;
  email: string;
  ghinNumber: string;
  vipCardNumber: string;
  transactionNumber: string;
  orderNumber: string;
  dealName: string;
  amount: string;
  amountRaw: number;
  vipPackage: string;
  createDate: string;
  updateDate?: string;
  type?: "deal" | "giftCard";
};

const formatDateTime = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const BRAND_GREEN = "#023822";

const AllRefunds = () => {
  const [startDate, setStartDate] = useState<string>("");
  const [endDate,   setEndDate]   = useState<string>("");
  const [refunds,   setRefunds]   = useState<Refund[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [search,    setSearch]    = useState("");
  const [pageSize,  setPageSize]  = useState<20 | 50 | 100>(20);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchRefunds = async (start: string, end: string) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getAdminRefunds(start, end);
      if (resp.success) {
        setRefunds(resp.refunds);
      } else {
        setError(resp.message || "Failed to load refunds.");
        setRefunds([]);
      }
    } catch {
      setError("Network error. Please try again.");
      setRefunds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds(startDate, endDate);
  }, [startDate, endDate]);

  useEffect(() => { setCurrentPage(1); }, [search, refunds, pageSize]);

  const filtered = refunds.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.firstName.toLowerCase().includes(q) ||
      r.lastName.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      r.ghinNumber.toLowerCase().includes(q) ||
      r.vipCardNumber.toLowerCase().includes(q) ||
      r.transactionNumber.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(currentPage, totalPages);
  const paginated  = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
        <div>
          <h4 className="mb-1 fw-bold" style={{ color: "#023822", letterSpacing: "-0.3px" }}>All Refunds</h4>
          <p className="mb-0" style={{ fontSize: "0.82rem", color: "#8a9199" }}>
            <span style={{ fontWeight: 600, color: "#495057" }}>{filtered.length}</span> result{filtered.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={() => fetchRefunds(startDate, endDate)}
          disabled={loading}
          style={{ backgroundColor: "#023822", color: "#fff", border: "none", borderRadius: 8, padding: "0.35rem 1rem", fontSize: "0.82rem", fontWeight: 500 }}
        >
          <i className="bi bi-arrow-clockwise me-1" />{loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {/* Filters */}
      <div style={{ background: "#f8f9fa", borderRadius: 12, padding: "0.85rem 1rem", marginBottom: "1.25rem", border: "1px solid #e9ecef" }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search name, email, GHIN, VIP card, transaction #…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 240, flex: "1 1 240px", maxWidth: 380, borderRadius: 8 }}
          />
          <div className="d-flex align-items-center gap-1">
            <label className="form-label mb-0 small text-muted text-nowrap">From</label>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{ width: 150, borderRadius: 8 }}
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="d-flex align-items-center gap-1">
            <label className="form-label mb-0 small text-muted text-nowrap">To</label>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{ width: 150, borderRadius: 8 }}
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {/* Table */}
      {!error && (
        <div style={{ borderRadius: 12, border: "1px solid #e9ecef", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
        <div className="table-responsive">
          <table
            className="table table-hover align-middle mb-0"
            style={{ fontSize: "0.84rem" }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Type</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>ID</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>First Name</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Last Name</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Email</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>GHIN #</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>VIP Card #</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Package</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Amount</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Transaction #</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Order #</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={12} className="text-center py-4 text-muted">
                    <div className="spinner-border spinner-border-sm me-2" role="status" />
                    Loading refunds…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={12} className="text-center py-4 text-muted">
                    {search
                      ? "No refunds match your search."
                      : "No refunds found for this date range."}
                  </td>
                </tr>
              )}
              {!loading &&
                paginated.map((r) => (
                  <tr key={r.dealId}>
                    <td>
                      {r.type === "giftCard" ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", backgroundColor: "#fff3cd", color: "#856404", border: "1px solid #ffc107", borderRadius: 6, padding: "0.15rem 0.5rem", fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap" }}>
                          <i className="bi bi-gift" />Gift Card
                        </span>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7", borderRadius: 6, padding: "0.15rem 0.5rem", fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap" }}>
                          <i className="bi bi-person-badge" />Membership
                        </span>
                      )}
                    </td>
                    <td>
                      {r.contactId ? (
                        <a
                          href={hubspotRecordUrl("contact", r.contactId)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-decoration-none"
                          style={{ fontFamily: "monospace", fontSize: "0.8rem", color: BRAND_GREEN }}
                        >
                          {r.contactId}
                        </a>
                      ) : "—"}
                    </td>
                    <td>{r.firstName || "—"}</td>
                    <td>{r.lastName  || "—"}</td>
                    <td>
                      {r.email ? (
                        <a
                          href={`mailto:${r.email}`}
                          className="text-decoration-none"
                          style={{ color: BRAND_GREEN }}
                        >
                          {r.email}
                        </a>
                      ) : "—"}
                    </td>
                    <td>{r.ghinNumber    || "—"}</td>
                    <td>{r.vipCardNumber || "—"}</td>
                    <td>{r.vipPackage    || "—"}</td>
                    <td className="fw-semibold" style={{ color: "#dc3545" }}>
                      {r.amountRaw ? `-$${r.amountRaw.toFixed(2)}` : "—"}
                    </td>
                    <td>
                      <span style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                        {r.transactionNumber || "—"}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                        {r.orderNumber || "—"}
                      </span>
                    </td>
                    <td className="text-nowrap text-muted">{formatDateTime(r.updateDate || r.createDate)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        </div>
      )}
      {/* Pagination */}
      {!error && !loading && filtered.length > 0 && (
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
          <div className="d-flex align-items-center gap-2">
            <span className="small text-muted">Rows per page:</span>
            {([20, 50, 100] as const).map((n) => (
              <button key={n} type="button" className="btn btn-sm"
                style={{ minWidth: 40, backgroundColor: pageSize === n ? BRAND_GREEN : "transparent", color: pageSize === n ? "#fff" : "#495057", border: `1px solid ${pageSize === n ? BRAND_GREEN : "#dee2e6"}`, fontWeight: pageSize === n ? 600 : 400 }}
                onClick={() => setPageSize(n)}>{n}</button>
            ))}
          </div>
          <div className="d-flex align-items-center gap-1">
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={safePage === 1} onClick={() => setCurrentPage(1)}>«</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={safePage === 1} onClick={() => setCurrentPage((p) => p - 1)}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
              .reduce<(number | "…")[]>((acc, p, i, arr) => { if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push("…"); acc.push(p); return acc; }, [])
              .map((p, i) => p === "…"
                ? <span key={`e-${i}`} className="px-1 small text-muted">…</span>
                : <button key={p} type="button" className="btn btn-sm"
                    style={{ minWidth: 34, backgroundColor: p === safePage ? BRAND_GREEN : "transparent", color: p === safePage ? "#fff" : "#495057", border: `1px solid ${p === safePage ? BRAND_GREEN : "#dee2e6"}`, fontWeight: p === safePage ? 600 : 400 }}
                    onClick={() => setCurrentPage(p as number)}>{p}</button>
              )}
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={safePage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>›</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={safePage === totalPages} onClick={() => setCurrentPage(totalPages)}>»</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllRefunds;
